import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Alert,
  Badge,
  Card,
  cn,
  ConfirmModal,
  Icon,
  Modal,
  Page,
  RelativeDateTime,
  SimpleSelect,
  Skeleton,
} from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { FormEventHandler, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import EmptyStateAction from '~/components/EmptyStateAction';
import {
  usePlatformMetafields,
  useStoreMetafields,
  useStoreMetafieldsCreate,
  useStoreMetafieldsDelete,
} from '~/features/settings/hooks/useStoreMetafields';

function MetafieldForm({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const { data: storeMetafields = [] } = useStoreMetafields();
  const {
    data: platformMetafields = [],
    isLoading,
    isError,
  } = usePlatformMetafields(show);
  const { mutate: createStoreMetafield, isPending } =
    useStoreMetafieldsCreate();

  const { control, handleSubmit, reset, watch } = useForm({
    resolver: zodResolver(CrewMerchantUi.storeMetafieldCreateSchema),
  });

  const options = platformMetafields.filter(
    (pm) =>
      !storeMetafields.some(
        (sm) => sm.namespace === pm.namespace && sm.key === pm.key,
      ),
  );
  const selected = options.find(
    (m) => m.idFromPlatform === watch('idFromPlatform'),
  );

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((create) => {
      createStoreMetafield(create, {
        onSuccess: () => {
          reset();
          onClose();
        },
      });
    })(event).catch(console.error);
  };

  return (
    <Modal
      title="Add Metafield"
      description="Select a metafield to track values for within Corso."
      show={show}
      onClose={onClose}
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        {isError && (
          <Alert
            variant="critical"
            heading="Metafields could not be loaded from Shopify. Please try again."
          />
        )}

        {!isLoading && !isError && platformMetafields.length === 0 && (
          <Alert
            variant="info"
            heading="There are no single or multi-line text metafields on product variants in Shopify."
          />
        )}

        {!isLoading &&
          platformMetafields.length > 0 &&
          options.length === 0 && (
            <Alert
              variant="info"
              heading="No untracked single or multi-line text variant metafields found in Shopify."
            />
          )}

        {(isLoading || options.length > 0) && (
          <>
            <Controller
              name="idFromPlatform"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <SimpleSelect
                  label="Variant Metafield"
                  options={options.map((m) => ({
                    value: m.idFromPlatform,
                    label: m.name,
                  }))}
                  value={value}
                  onChange={onChange}
                  disabled={isLoading}
                  placeholder={
                    isLoading ? 'Loading metafields…' : 'Select a metafield'
                  }
                  details={
                    selected
                      ? (selected.description ??
                        `${selected.namespace}.${selected.key}`)
                      : 'Single and multi-line text metafields on product variants.'
                  }
                  error={error?.message}
                />
              )}
            />

            <Action
              variant="primary"
              type="submit"
              loading={isPending}
              disabled={!selected}
            >
              Add Metafield
            </Action>
          </>
        )}
      </form>
    </Modal>
  );
}

function DeleteMetafield({
  storeMetafield,
}: {
  storeMetafield: CrewMerchantUi.StoreMetafield;
}) {
  const { mutate: deleteStoreMetafield, isPending } =
    useStoreMetafieldsDelete();
  const [showConfirmation, setShowConfirmation] = useState(false);

  return (
    <>
      <Action
        icon="remove"
        variant="ghost"
        accessibilityLabel={`Delete ${storeMetafield.name}`}
        onClick={() => setShowConfirmation(true)}
      />

      <ConfirmModal
        title="Delete Metafield"
        prompt="Are you sure you'd like to delete this metafield within Corso?"
        confirmText="Delete"
        cancelText="Cancel"
        show={showConfirmation}
        loading={isPending}
        onConfirm={() => {
          deleteStoreMetafield(storeMetafield);
          setShowConfirmation(false);
        }}
        onCancel={() => setShowConfirmation(false)}
      />
    </>
  );
}

function MetafieldsSkeleton() {
  return (
    <div className="flex gap-2">
      <Skeleton.Rectangle height="36px" width="100%" />
      <Skeleton.Rectangle height="36px" width="44px" />
    </div>
  );
}

export default function MetafieldSettingsPage() {
  const {
    data: storeMetafields = [],
    isLoading,
    isError,
  } = useStoreMetafields();
  const [showForm, setShowForm] = useState(false);

  return (
    <Page
      title="Metafields"
      primaryAction={{
        onAction: () => setShowForm(true),
        icon: 'add',
        content: 'Add Metafield',
      }}
    >
      <div className="flex flex-col gap-4">
        <Alert
          variant="tip"
          heading="Only Product Variant metafields with a single-line or multi-line text type are supported.
"
        />
        <Card>
          <Skeleton
            instances={1}
            skeleton={MetafieldsSkeleton}
            isLoading={isLoading}
          >
            {isError && (
              <Alert
                variant="critical"
                heading="Metafields could not be loaded. Please try again."
              />
            )}

            {!isError && storeMetafields.length === 0 && (
              <EmptyStateAction.Button
                onClick={() => setShowForm(true)}
                icon={<Icon icon="database" />}
                label="Add a metafield"
                description="Add a metafield from Shopify, to be tracked within the Corso Platform."
              />
            )}

            {storeMetafields.length > 0 && (
              <ul>
                {storeMetafields.map((storeMetafield, index) => (
                  <li
                    key={storeMetafield.id}
                    className={cn(
                      'flex items-center justify-between gap-2 border-b py-3',
                      index === 0 && 'pt-0',
                      index === storeMetafields.length - 1 && 'border-0 pb-0',
                    )}
                  >
                    <div className="flex min-w-0 flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-medium text-neutral-700">
                          {storeMetafield.name}
                        </p>
                        <Badge>
                          {storeMetafield.namespace}.{storeMetafield.key}
                        </Badge>
                      </div>
                      <p className="text-xs text-neutral-500">
                        Added{' '}
                        <RelativeDateTime dateTime={storeMetafield.createdOn} />
                      </p>
                    </div>
                    <DeleteMetafield storeMetafield={storeMetafield} />
                  </li>
                ))}
              </ul>
            )}
          </Skeleton>

          <MetafieldForm show={showForm} onClose={() => setShowForm(false)} />
        </Card>
      </div>
    </Page>
  );
}
